import { useState, useEffect, useMemo } from 'react';
import { TAB_ITEMS } from '../constant';
import useFetchProvidedJobPost from '../api/useFetchProvidedJobPost';
import ProvidedJobPostsHeader from '../components/ProvidedJobPostsHeader';
import ProvidedJobPostsInfoCard from '../components/ProvidedJobPostsInfoCard';
import ProvidedJobPostsSuggestedCandidatesCard from '../components/ProvidedJobPostsSuggestedCandidatesCard';
import ProvidedJobPostsJobChangeProcessesCard from '../components/ProvidedJobPostsJobChangeProcessesCard';
import AllianceFeeSection from '../components/AllianceFeeSection';
import ProvidedJobPostsSettingsCard from '../components/ProvidedJobPostsSettingsCard';
import { useSetBreadcrumbTitle } from '@/hooks/useBreadcrumb';
import clsx from 'clsx';
import NotFoundPage from '../../errors/NotFoundPage';
import useFetchIam from '@/features/agent/api/useFetchIam';

type Props = {
  id: string;
};

export default function ProvidedJobPostDetailTemplate({ id }: Props) {
  const { data: providedJobPost, isLoading, isError } = useFetchProvidedJobPost(Number(id));
  const { data: iam } = useFetchIam();
  const isJobOnlyPlan = iam?.plan_type === 'job_only';
  const isAllianceEnabled = iam?.is_alliance_enabled ?? false;

  const visibleTabItems = useMemo(() => {
    // job_only プランは「求人情報」「その他設定」のみ表示する
    const baseItems = isJobOnlyPlan
      ? TAB_ITEMS.filter((item) => ['info', 'settings'].includes(item.value))
      : TAB_ITEMS;
    // アライアンス機能が無効な組織ではアライアンスタブを除外する
    return isAllianceEnabled ? baseItems : baseItems.filter((item) => item.value !== 'alliance');
  }, [isJobOnlyPlan, isAllianceEnabled]);

  // パンくずリストに求人タイトルを設定
  useSetBreadcrumbTitle(providedJobPost?.title);

  const getTabFromUrl = (): (typeof TAB_ITEMS)[number]['value'] => {
    if (typeof window === 'undefined') return 'info';

    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get('tab');

    const availableValues = visibleTabItems.map((item) => item.value);

    return tabParam && availableValues.includes(tabParam as (typeof TAB_ITEMS)[number]['value'])
      ? (tabParam as (typeof TAB_ITEMS)[number]['value'])
      : 'info';
  };

  const [tab, setTab] = useState<(typeof TAB_ITEMS)[number]['value']>(getTabFromUrl());

  const handleTabChange = (newTab: (typeof TAB_ITEMS)[number]['value']) => {
    setTab(newTab);

    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      url.searchParams.set('tab', newTab);
      window.history.replaceState({}, '', url.toString());
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      setTab(getTabFromUrl());
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, []);

  // visibleTabItems が変化（iam の取得完了など）したら現在の tab が利用可能か再評価し、
  // URL の ?tab= とずれていれば取り直す。これがないと `?tab=alliance` 直リンクで来ても
  // 初回マウント時点ではアライアンスタブが除外されており info にフォールバックしてしまう
  useEffect(() => {
    const availableValues = visibleTabItems.map((item) => item.value);
    if (!availableValues.includes(tab)) {
      setTab(getTabFromUrl());
    }
  }, [visibleTabItems, tab]);

  if (isLoading) {
    return (
      <div className="flex h-[calc(100vh-60px)] items-center justify-center overflow-scroll">
        <div className="text-center">
          <div className="mb-4 inline-block size-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
          <p className="text-gray-500">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (isError || !providedJobPost) {
    return <NotFoundPage />;
  }

  return (
    <div className="hidden-scrollbar h-[calc(100vh-60px)] overflow-scroll">
      <ProvidedJobPostsHeader
        jobPost={providedJobPost}
        counterCache={providedJobPost?.counter_cache}
      />

      {/* タブナビゲーション - 求人情報、打診管理、選考管理、その他設定のタブ切り替え */}
      <div className="sticky top-0 z-10 flex items-center justify-around bg-white pt-md">
        {visibleTabItems.map((item) => (
          <button
            type="button"
            key={item.value}
            className={clsx(
              'h-[44px] w-full cursor-pointer border-0 border-b border-solid border-b-border pb-px text-sm hover:font-bold hover:text-primary',
              tab === item.value && 'border-b-2 border-b-primary pb-0! font-bold text-primary'
            )}
            onClick={() => handleTabChange(item.value)}
          >
            {item.label}
          </button>
        ))}
      </div>

      <section className={clsx('w-full flex-1 px-lg pt-sm pb-lg')}>
        {tab === 'info' && <ProvidedJobPostsInfoCard jobPost={providedJobPost} />}
        {tab === 'suggested_candidates' && !isJobOnlyPlan && (
          <ProvidedJobPostsSuggestedCandidatesCard
            jobPostId={Number(id)}
            companyName={providedJobPost?.company?.name ?? ''}
            position={providedJobPost?.job_position_name ?? ''}
            location={providedJobPost?.working_location ?? ''}
          />
        )}
        {tab === 'job_change_processes' && !isJobOnlyPlan && (
          <ProvidedJobPostsJobChangeProcessesCard jobPostId={Number(id)} />
        )}
        {tab === 'alliance' && isAllianceEnabled && (
          <AllianceFeeSection jobPost={providedJobPost} />
        )}
        {tab === 'settings' && <ProvidedJobPostsSettingsCard jobPost={providedJobPost} />}
      </section>
    </div>
  );
}
